import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Typography, Modal, Grid, Spin } from 'antd';
import { PhoneOutlined, PhoneFilled } from '@ant-design/icons';
import { PhoneInput } from 'react-international-phone';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumberWithError, isValidPhoneNumber } from 'libphonenumber-js';
import { useTimer } from '../../hooks/useTimer';
import { formatTime } from '../../utils/formatTime';
import createCall from '../../utils/call/createCall';
import connectCall from '../../utils/call/connectCall';
import { toast } from 'react-hot-toast';
import { getCurrentUser } from '../../utils/userStorage';
import 'react-international-phone/style.css';

const { Paragraph, Title } = Typography;
const { useBreakpoint } = Grid;

export default function CallDialer({ onCallEnded, patient = null }) {
  const { t } = useTranslation();
  const screens = useBreakpoint();

  // pull these *before* any hooks
  const patientPhone = patient?.getMobileNumber() ?? null;
  const patientUuid  = patient?.getUUID()        ?? null;
  const patientName  = patient?.getFullName()    ?? null;

  // always call the hook
  const [form] = Form.useForm();

  const [callStatus, setCallStatus] = useState('idle');        // 'idle' | 'connecting' | 'connected'
  const [activeCall, setActiveCall] = useState(null);
  const [isCallModalVisible, setIsCallModalVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const timer = useTimer(callStatus === 'connected', hasStarted);
  const didAutoDial = useRef(false);

  const currentUser    = getCurrentUser();
  const defaultCountry = (currentUser?.survey_country || currentUser?.calls_country || 'MX').toUpperCase();

  function validatePhone(raw) {
    console.log('[validatePhone] raw input:', raw, 'default country:', defaultCountry);
    if (!raw) {
      setPhoneError(t('calls.invalid_number'));
      return false;
    }
    try {
      // if it starts with "+" parse as international, otherwise give a country hint
      const parsed = raw.trim().startsWith('+')
        ? parsePhoneNumberWithError(raw)
        : parsePhoneNumberWithError(raw, defaultCountry);

      if (!isValidPhoneNumber(parsed.number)) {
        setPhoneError(t('calls.invalid_number'));
        return false;
      }
      setPhoneError('');
      return true;
    } catch (err) {
      console.log('[validatePhone] ❌ parsing error:', err.code || err.message);
      setPhoneError(t('calls.invalid_number'));
      return false;
    }
  }

  async function startCall(raw) {
    setCallStatus('connecting');
    setIsCallModalVisible(true);

    try {
      const parsed = raw.trim().startsWith('+')
        ? parsePhoneNumberWithError(raw)
        : parsePhoneNumberWithError(raw, defaultCountry);

      const { twilio_token, call_uuid } = await createCall(
        parsed.format('E.164'),
        patientUuid
      );

      const call = await connectCall({
        twilioToken:  twilio_token,
        callUUID:     call_uuid,
        parsedNumber: parsed,
      });

      setActiveCall(call);
      setCallStatus('connected');
      call.on('disconnect', endCall);
      call.on('cancel',     endCall);
    } catch (err) {
      console.error('startCall error:', err);
      setIsCallModalVisible(false);
      setCallStatus('idle');
      toast.error(t('calls.call_error'));
    } finally {
      setHasStarted(true);
    }
  }

  function handleSubmit() {
    const raw = form.getFieldValue('to_number') || '';
    if (!validatePhone(raw)) return;
    startCall(raw);
  }

  function endCall() {
    if (activeCall) {
      activeCall.off('disconnect', endCall);
      activeCall.off('cancel',     endCall);
      activeCall.disconnect();
    }
    setActiveCall(null);
    setCallStatus('idle');
    setHasStarted(false);
    setIsCallModalVisible(false);
    onCallEnded?.();
  }

  function handlePhoneChange(val) {
    setPhoneError('');
    form.setFieldsValue({ to_number: val });
  }

  // auto‐dial once if a patientPhone is provided
  useEffect(() => {
    if (!patientPhone || didAutoDial.current) return;

    // validate first
    if (!validatePhone(patientPhone)) {
      // show a toast and close the dialer so that clicking again retries
      toast.error(t('calls.invalid_patient_number'));
      setTimeout(() => onCallEnded?.(), 0);
      return;
    }

    // only mark as dialed when we actually start
    didAutoDial.current = true;
    startCall(patientPhone);
  }, [patientPhone]);

  return (
    <>
      {/* hidden form keeps our `form` hook connected */}
      {patientPhone && <Form form={form} style={{ display: 'none' }} />}

      {/* only show manual dialer when there's NO patientPhone */}
      {!patientPhone && (
        <div style={{
          maxWidth: '500px', margin: '20px auto', background: '#80808020',
          padding: '20px', borderRadius: '16px',
        }}>
          <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            style={{ maxWidth: '400px', margin: '0 auto' }}
          >
            <Form.Item
              label={t('calls.dialer_title')}
              name="to_number"
              validateStatus={phoneError ? 'error' : ''}
              help={phoneError}
              style={{ marginBottom: 0 }}
              labelCol={{ style: { paddingBottom: 5 } }}
            >
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <div style={{ flex: 1, width: '100%' }}>
                  <PhoneInput
                    defaultCountry={defaultCountry.toLowerCase()}
                    forceDialCode
                    onChange={handlePhoneChange}
                    placeholder="55 1234 5678"
                    style={{ width: '100%' }}
                    inputStyle={{ width: '100%', padding: '12px 16px', fontSize: 16 }}
                  />
                </div>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={callStatus === 'connecting'}
                  icon={<PhoneOutlined className="rotated-icon" />}
                  size={screens.xs ? 'small' : 'large'}
                  style={{
                    borderRadius: '50%', padding: 0, background: 'var(--main-color)',
                    border: 'none', boxShadow: '0 4px 12px rgba(76,175,80,0.3)',
                  }}
                />
              </div>
            </Form.Item>
          </Form>
        </div>
      )}

      <Modal
        open={isCallModalVisible}
        footer={null}
        closable={false}
        centered
        maskClosable={false}
        width={360}
      >
        <div style={{
          textAlign: 'center', display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 24, background: '#80808012',
          padding: 20, borderRadius: 7,
        }}>
          <Title level={5} style={{ margin: 0 }}>
            {callStatus === 'connecting'
              ? t('calls.connecting')
              : `${t('calls.in_progress')} ${patientName || form.getFieldValue('to_number')}`
            }
          </Title>

          {callStatus === 'connecting'
            ? <Spin size="large" />
            : <div style={{ fontSize: 24, fontWeight: 100, marginBottom: 16 }}>
              {formatTime(timer)}
            </div>
          }

          {callStatus === 'connected' && (
            <>
              <Button
                type="primary" danger
                icon={<PhoneFilled style={{ transform: 'rotate(225deg)' }} />}
                onClick={endCall}
                size="large" shape="circle"
                style={{
                  marginTop: 20, width: 64, height: 64,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 24, background: '#ff4d4f', border: 'none',
                  boxShadow: '0 4px 12px rgba(255,77,79,0.3)',
                }}
              />
              <Paragraph type="secondary" style={{ margin: 0 }}>
                {t('calls.transcription_note')}
              </Paragraph>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}